/**
 * Map Navigator Service
 *
 * @module search/services
 * @version 1.0
 *
 * @description
 * Orquesta la navegación del mapa cuando se selecciona un resultado de búsqueda.
 * Maneja zoom, highlight, selección y apertura de paneles según el modo.
 */

import { MapEngineService } from '../../map-engine/MapEngineService';
import type { SearchHit, NavigationOptions } from '../types';

/**
 * Servicio de navegación del mapa
 */
export class MapNavigatorService {
  private static instance: MapNavigatorService;
  private mapEngine: MapEngineService;
  private currentHighlightTimeout: NodeJS.Timeout | null = null;

  private constructor() {
    this.mapEngine = MapEngineService.getInstance();
  }

  public static getInstance(): MapNavigatorService {
    if (!MapNavigatorService.instance) {
      MapNavigatorService.instance = new MapNavigatorService();
    }
    return MapNavigatorService.instance;
  }

  /**
   * Navega a un resultado de búsqueda
   *
   * @param hit - Resultado de búsqueda
   * @param options - Opciones de navegación
   */
  async navigateToHit(hit: SearchHit, options: Partial<NavigationOptions> = {}): Promise<void> {
    // Aplicar preset según mode
    const fullOptions = this.applyModePreset(options);

    console.log('[MapNavigator] Navigating to hit:', hit.label, 'mode:', fullOptions.mode);

    // Limpiar highlight anterior
    this.clearHighlight();

    try {
      // 1. ZOOM (si corresponde)
      if (fullOptions.zoom) {
        await this.performZoom(hit, fullOptions.targetZoom);
      }

      // 2. HIGHLIGHT (si corresponde)
      if (fullOptions.highlight) {
        this.performHighlight(hit, fullOptions);
      }

      // 3. SELECCIÓN (si corresponde)
      if (fullOptions.selectFeature && fullOptions.emitEvents) {
        this.emitSelectionEvent(hit);
      }

      // 4. ABRIR PANEL (si corresponde)
      if (fullOptions.openPanel) {
        this.openEntityPanel(hit);
      }

      console.log('[MapNavigator] Navigation completed');
    } catch (error) {
      console.error('[MapNavigator] Error navigating to hit:', error);
      throw error;
    }
  }

  /**
   * Aplica preset según el mode de navegación
   */
  private applyModePreset(options: Partial<NavigationOptions>): Required<NavigationOptions> {
    const base: Required<NavigationOptions> = {
      mode: options.mode || 'select',
      zoom: true,
      highlight: true,
      selectFeature: true,
      openPanel: true,
      emitEvents: true,
      autoCleanHighlight: true,
      cleanHighlightDelay: 10000,
      autoSwitchModule: false,
      autoShowLayer: true,
      targetZoom: null,
    };

    // Override base con options proporcionadas
    const merged = { ...base, ...options };

    // Aplicar preset según mode
    switch (merged.mode) {
      case 'preview':
        // Solo highlight suave, sin zoom ni selección
        return {
          ...merged,
          zoom: false,
          highlight: true,
          selectFeature: false,
          openPanel: false,
          emitEvents: false,
        };

      case 'navigate':
        // Zoom + highlight, sin selección
        return {
          ...merged,
          zoom: true,
          highlight: true,
          selectFeature: false,
          openPanel: false,
          emitEvents: false,
        };

      case 'select':
        // Todo: zoom + highlight + selección + panel
        return {
          ...merged,
          zoom: true,
          highlight: true,
          selectFeature: true,
          openPanel: true,
          emitEvents: true,
        };

      default:
        return merged;
    }
  }

  /**
   * Realiza zoom al resultado
   */
  private async performZoom(hit: SearchHit, targetZoom: number | null): Promise<void> {
    if (hit.bounds) {
      // Si tiene bounds (LineString/Polygon), fitBounds
      console.log('[MapNavigator] FitBounds:', hit.bounds);
      this.mapEngine.fitBounds(hit.bounds as [number, number, number, number], {
        padding: 50,
        maxZoom: 16,
        duration: 2000,
      });
    } else {
      // Si es Point, flyTo
      const coords = (hit.geometry as GeoJSON.Point).coordinates as [number, number];
      const zoom = targetZoom || this.getDefaultZoomForKind(hit.kind);

      console.log('[MapNavigator] FlyTo:', coords, 'zoom:', zoom);
      this.mapEngine.flyTo({
        center: coords,
        zoom,
        duration: 2000,
        padding: 50,
      });
    }

    // Esperar a que termine la animación
    await new Promise((resolve) => setTimeout(resolve, 2100));
  }

  /**
   * Realiza highlight del resultado
   */
  private performHighlight(hit: SearchHit, options: Required<NavigationOptions>): void {
    const feature = {
      type: 'Feature' as const,
      geometry: hit.geometry,
      properties: hit.properties || {},
    };

    this.mapEngine.highlightFeature(feature, hit.layerId || 'search-result');

    // Auto-limpiar highlight si está configurado
    if (options.autoCleanHighlight) {
      this.currentHighlightTimeout = setTimeout(() => {
        this.clearHighlight();
      }, options.cleanHighlightDelay);
    }
  }

  /**
   * Limpia el highlight actual
   */
  clearHighlight(): void {
    if (this.currentHighlightTimeout) {
      clearTimeout(this.currentHighlightTimeout);
      this.currentHighlightTimeout = null;
    }
    this.mapEngine.clearHighlight();
  }

  /**
   * Emite evento de selección de feature
   */
  private emitSelectionEvent(hit: SearchHit): void {
    // TODO: Integrar con EventBus cuando sea necesario
    console.log('[MapNavigator] Feature selected:', hit.entityId);

    // Emitir evento custom
    const event = new CustomEvent('map:feature-selected', {
      detail: {
        feature: {
          type: 'Feature',
          geometry: hit.geometry,
          properties: hit.properties,
        },
        layerId: hit.layerId,
        source: 'search',
      },
    });
    window.dispatchEvent(event);
  }

  /**
   * Abre el panel de detalles de la entidad
   */
  private openEntityPanel(hit: SearchHit): void {
    if (!hit.entityId || !hit.entityType) {
      console.log('[MapNavigator] No entity to open panel for');
      return;
    }

    // TODO: Integrar con sistema de paneles cuando sea necesario
    console.log('[MapNavigator] Opening panel for:', hit.entityType, hit.entityId);

    // Emitir evento custom para que otros componentes lo manejen
    const event = new CustomEvent('map:open-entity-panel', {
      detail: {
        entityType: hit.entityType,
        entityId: hit.entityId,
      },
    });
    window.dispatchEvent(event);
  }

  /**
   * Obtiene el zoom por defecto según el tipo de resultado
   */
  private getDefaultZoomForKind(kind: string): number {
    switch (kind) {
      case 'coordinate':
        return 16;
      case 'entity':
        return 17;
      case 'address':
        return 16;
      case 'network-node':
        return 18;
      case 'network-edge':
        return 16;
      default:
        return 15;
    }
  }
}

// Export singleton instance
export const mapNavigatorService = MapNavigatorService.getInstance();
